import firebase from "./firebase";
import { firestore as db } from "./firebase";
import { BaseModel } from "./models";
import { AuthenticatedUser } from "./users";
import { Statement, getStatuses } from "./statements";

export const COLLECTION_STATEMENTS: string = "statements";
export const COLLECTION_STATEMENT_VOTES: string = "statement_votes";

export const AGREE: VoteType = "agree";
export const DISAGREE: VoteType = "disagree";
export const NO_OPINION: VoteType = "no opinion";
export type VoteType = "agree" | "disagree" | "no opinion";

// The StatementVote type defines how a statement vote is stored in the Firestore database.
export type StatementVote = BaseModel & {
  vote: VoteType;
  user: {
    userId: string;
    displayname: string;
  };
  statement: {
    statementId: string;
    title: string;
    status: string;
    slug: string;
  };
};

export function getStatementVote(
  statementId: string,
  userId: string,
  setStatementVote: (vote: VoteType | null) => void
) {
  return db
    .collection(COLLECTION_STATEMENT_VOTES)
    .where("user.userId", "==", userId)
    .where("statement.statementId", "==", statementId)
    .limit(1)
    .onSnapshot((snapshot) => {
      let data: StatementVote[] = [];
      snapshot.forEach((doc) => data.push({ ...doc.data() } as any));
      setStatementVote(data && data.length > 0 ? data[0].vote : null);
    });
}

export async function voteStatement(
  statement: Statement,
  user: AuthenticatedUser,
  vote: VoteType
): Promise<boolean> {
  const statementVoteId = `${statement.id}-${user.id}`;

  const statementVoteRef = db
    .collection(COLLECTION_STATEMENT_VOTES)
    .doc(statementVoteId);

  try {
    await db.runTransaction(async (transaction: any) => {
      const statementVote = await transaction.get(statementVoteRef);

      if (statementVote.exists) {
        if (statementVote.data().vote !== vote)
          transaction.update(statementVoteRef, {
            vote,
            timestamp: firebase.firestore.Timestamp.now(),
          });
      } else {
        const newVoteDocument = createStatementVoteObject(
          statementVoteId,
          vote,
          statement,
          user
        );
        transaction.set(statementVoteRef, newVoteDocument);
      }
    });
    return true;
  } catch (error) {
    return false;
  }
}

function createStatementVoteObject(
  id: string,
  vote: VoteType,
  statement: Statement,
  user: AuthenticatedUser
) {
  return {
    id,
    timestamp: firebase.firestore.Timestamp.now(),
    vote,
    statement: {
      statementId: statement.id,
      title: statement.title,
      status: statement.status,
      slug: statement.slug,
    },
    user: {
      userId: user.id,
      displayname: user.displayname,
    },
  };
}

export async function getStatementsUserHasVotedOn(
  userId: string,
  isAdmin: boolean
) {
  const data = await db
    .collection(COLLECTION_STATEMENT_VOTES)
    .where("user.userId", "==", userId)
    .where("statement.status", "in", getStatuses(isAdmin))
    .get();

  let statements: StatementVote[] = [];
  data.forEach((doc) => statements.push({ ...doc.data() } as any));
  return statements;
}
